import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { cn } from '@/lib/cn'
import { isDeferredResolution } from '../../types'
import type { Resolution } from '../../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (resolution: Resolution, reason: string) => void
  /** True when the parent mutation is in flight; disables the confirm button. */
  pending?: boolean
}

// `insufficient_evidence` and `customer_confirmed_legitimate` have no
// backend closed_* terminal yet (deferred pending a product
// decision — see `isDeferredResolution` in types.ts). They stay
// visible-but-disabled rather than silently disappearing, so an
// analyst who's used to seeing them knows the option exists but
// isn't available yet, instead of wondering whether it was removed
// by mistake or guessing "Other" is now the intended substitute.
const OPTIONS: ReadonlyArray<{ value: Resolution; label: string; warn?: string }> = [
  { value: 'false_positive', label: 'False positive' },
  {
    value: 'fraud_confirmed',
    label: 'Confirmed fraud',
    warn: 'Triggers SAR drafting workflow (AF-136).',
  },
  {
    value: 'insufficient_evidence',
    label: 'Insufficient evidence',
    warn: 'Not available yet — pending a product decision on how this resolution is recorded. Use "Other" with a reason for now.',
  },
  { value: 'duplicate', label: 'Duplicate' },
  {
    value: 'customer_confirmed_legitimate',
    label: 'Customer confirmed legitimate',
    warn: 'Not available yet — pending a product decision on how this resolution is recorded. Use "Other" with a reason for now.',
  },
  { value: 'other', label: 'Other (requires reason)' },
]

export function CloseDialog({ open, onOpenChange, onConfirm, pending }: Props) {
  const [resolution, setResolution] = useState<Resolution>('false_positive')
  const [reason, setReason] = useState('')
  const reasonRequired = resolution === 'other'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close alert</DialogTitle>
          <DialogDescription>
            Choose a resolution. This is recorded in the audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {OPTIONS.map((opt) => {
            const deferred = isDeferredResolution(opt.value)
            return (
              <label
                key={opt.value}
                className={cn(
                  'flex items-start gap-2 rounded border px-3 py-2 text-sm',
                  deferred
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer',
                  !deferred && resolution === opt.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border',
                  !deferred && resolution !== opt.value && 'hover:bg-muted',
                )}
              >
                <input
                  type="radio"
                  name="resolution"
                  value={opt.value}
                  checked={resolution === opt.value}
                  disabled={deferred}
                  aria-disabled={deferred}
                  onChange={() => setResolution(opt.value)}
                  className="mt-1 h-3.5 w-3.5 accent-primary disabled:cursor-not-allowed"
                />
                <div className="flex flex-col">
                  <span className="font-medium">{opt.label}</span>
                  {opt.warn && (
                    <span className="mt-0.5 text-xs text-status-pending">{opt.warn}</span>
                  )}
                </div>
              </label>
            )
          })}
        </div>

        {reasonRequired && (
          <div className="flex flex-col gap-1">
            <Label htmlFor="close-reason">Reason</Label>
            <textarea
              id="close-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Provide a brief explanation (required for ‘Other’)…"
              className="min-h-[80px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={resolution === 'fraud_confirmed' ? 'destructive' : 'primary'}
            disabled={
              pending ||
              isDeferredResolution(resolution) ||
              (reasonRequired && reason.trim().length < 5)
            }
            onClick={() => onConfirm(resolution, reason)}
          >
            Close alert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
